// src/data/slideDecks.js
//
// RULING 311 (Bob 2026-10-13): the records become slides. Each builder turns ONE record the viewer may already read into a
// deck for src\components\SlideDeck.js. Pure: nothing is fetched here, nothing typed in, nothing stored - a deck is built
// when it is opened and shows what the record holds; where the record is silent, the deck says so (PD-0).
// A deck: { title, slides: [{ kicker, title, lines: [{ text, quote, cite, tone }], note }] }.

const s = (v) => (v == null ? '' : String(v)).trim();
const PLACEHOLDER = /^(⚙|âš™|Error|Could not|Generating)/;
const real = (v) => { const t = s(v); return t && !PLACEHOLDER.test(t) ? t : ''; };

// Long text packed into slides at paragraph/line breaks, never mid-line where it can be helped.
export const SLIDE_CHARS = 850;
export function packText(text, max) {
  const lim = max || SLIDE_CHARS;
  const out = [];
  let cur = [];
  let len = 0;
  for (const raw of String(text || '').split(/\n/)) {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) { if (cur.length && cur[cur.length - 1] !== '') { cur.push(''); } continue; }
    let rest = line;
    while (rest.length > lim) {   // one very long paragraph: break at the last sentence end before the limit
      const cut = Math.max(rest.lastIndexOf('. ', lim), rest.lastIndexOf('; ', lim), Math.floor(lim * 0.6));
      const head = rest.slice(0, cut + 1);
      if (len && len + head.length > lim) { out.push(cur); cur = []; len = 0; }
      cur.push(head.trim()); out.push(cur); cur = []; len = 0;
      rest = rest.slice(cut + 1).trim();
    }
    if (len && len + rest.length > lim) { out.push(cur); cur = []; len = 0; }
    cur.push(rest); len += rest.length + 1;
  }
  if (cur.some(x => x)) out.push(cur);
  return out.map(p => p.join('\n').replace(/\n{3,}/g, '\n\n').trim()).filter(Boolean);
}
function textSlides(kicker, title, text, note) {
  const parts = packText(text);
  return parts.map((p, k) => ({ kicker, title: parts.length > 1 ? title + ' (' + (k + 1) + ' of ' + parts.length + ')' : title, lines: [{ text: p }], note }));
}
const silent = (what) => ({ text: what, tone: 'warn' });

// ---------------------------------------------------------------------------------------------------------------------
// 1. HEARING PREPARATION (ruling 308) - a discipline case.
// ---------------------------------------------------------------------------------------------------------------------
export function hearingPrepDeck(c) {
  const d = c || {};
  const who = s(d.memberName) || 'The member';
  const title = 'Hearing preparation: ' + who;
  const slides = [{
    kicker: 'Discipline · preparation', title,
    lines: [
      { text: [s(d.disciplineType), s(d.suspensionDays) ? s(d.suspensionDays) + ' days' : '', s(d.craft), s(d.railroad), s(d.workLocation)].filter(Boolean).join(' · ') },
      { text: 'Agreement: ' + (s(d.applicableAgreement) || 'not on record') },
      { text: 'Case number: ' + (s(d.caseNumber) || 'not on record') },
      { text: 'Every meeting and the hearing are prepared for as though everything hangs in the balance.', tone: 'warn' },
    ],
  }];
  const dates = [
    { text: 'Letter dated: ' + (s(d.noticeDate) || 'not on record') },
    s(d.chargeReceivedOn || d.receivedOn || d.letterReceivedOn || d.receiptDate)
      ? { text: 'Received by the member: ' + s(d.chargeReceivedOn || d.receivedOn || d.letterReceivedOn || d.receiptDate) }
      : silent('The date the member RECEIVED the letter is not on record. Rule 36 clocks run from receipt, not from the letter\'s date.'),
    { text: 'Investigation deadline: ' + (s(d.investigationDeadline) || 'not on record') },
    // Ruling 308 (2026-10-14): the letter of intent and its own meeting clock, from receipt.
    ...(s(d.intentLetterDate) ? [{ text: 'Letter of intent dated: ' + s(d.intentLetterDate) + (s(d.intentReceivedOn) ? ', received ' + s(d.intentReceivedOn) : ' - receipt not on record') }] : []),
    ...(s(d.intentMeetingDue) ? [{ text: 'Intent meeting due by: ' + s(d.intentMeetingDue) }] : []),
  ];
  const h = d.hearingLog || {};
  if (s(h.hearingDate)) dates.push({ text: 'Hearing: ' + [s(h.hearingDate), s(h.hearingTime), s(h.location)].filter(Boolean).join(' · ') });
  for (const e of Array.isArray(d.hearingEvents) ? d.hearingEvents : []) {
    const line = [s(e.kind || e.type), s(e.date || e.on), s(e.time), s(e.note || e.reason)].filter(Boolean).join(' · ');
    if (line) dates.push({ text: line });
  }
  slides.push({ kicker: 'The dates', title: 'Letter, receipt and the clocks', lines: dates });
  const notice = real(d.noticeDescription);
  slides.push(...(notice ? textSlides('The letter', 'What the letter says', notice) : [{ kicker: 'The letter', title: 'What the letter says', lines: [silent('The letter\'s content is not on record.')] }]));
  const charges = real(d.chargesDescription);
  slides.push(...(charges ? textSlides('The charges', 'The charges', charges) : [{ kicker: 'The charges', title: 'The charges', lines: [silent('No charges are on record yet. An intent letter is not a charging letter.')] }]));
  for (const [key, kicker, ttl] of [['proceduralObjections', 'Preparation', 'Procedural objections'], ['investigationQuestions', 'Preparation', 'Questions for the investigation'],
    ['exhibitList', 'Preparation', 'Exhibits'], ['precedentResearch', 'Preparation', 'Precedent'], ['casePresentation', 'Preparation', 'The case as presented']]) {
    const t = real(d[key]);
    slides.push(...(t ? textSlides(kicker, ttl, t) : [{ kicker, title: ttl, lines: [silent('Not prepared yet.')] }]));
  }
  const awards = (Array.isArray(d.boardAwards) ? d.boardAwards : []).map(a => (typeof a === 'string' ? a : [s(a.award || a.number || a.title), s(a.board), s(a.holding || a.summary)].filter(Boolean).join(' · '))).filter(Boolean);
  if (awards.length) slides.push({ kicker: 'Precedent', title: 'Board awards on file', lines: awards.slice(0, 12).map(t => ({ text: t })) });
  const docs = (Array.isArray(d.documents) ? d.documents : []).map(x => [s(x.name), s(x.zone).replace(/_/g, ' ')].filter(Boolean).join(' · ')).filter(Boolean);
  slides.push({ kicker: 'The record', title: 'Documents on file', lines: docs.length ? docs.slice(0, 14).map(t => ({ text: t })) : [silent('No documents are on file.')] });
  if (s(h.hearingDate)) {
    slides.push({ kicker: 'The hearing', title: 'Who was there', lines: [
      { text: 'Hearing officer: ' + (s(h.hearingOfficer) || 'not on record') },
      { text: 'For the carrier: ' + (s(h.carriersPresent) || 'not on record') },
      { text: 'For the union: ' + (s(h.unionRepsPresent) || 'not on record') },
      { text: 'Witnesses: ' + (s(h.witnesses) || 'not on record') },
      { text: 'The member\'s plea: ' + (s(h.memberPlea) || 'not on record') },
    ] });
    const tn = real(h.transcriptNotes);
    if (tn) slides.push(...textSlides('The hearing', 'Notes of the hearing', tn));
  }
  return { title, slides };
}

// ---------------------------------------------------------------------------------------------------------------------
// 2. A CASE-FILE ASSESSMENT (ruling 309) - and the patterns across files. The General Chairperson's only.
// ---------------------------------------------------------------------------------------------------------------------
export function splitHeadings(text) {
  const parts = [];
  let cur = null;
  for (const line of String(text || '').split(/\n/)) {
    const m = /^\s*(?:#+\s*)?(\d{1,2})\.\s+([A-Z][A-Z0-9 ,'’&/()-]{3,})\s*$/.exec(line);
    if (m) { cur = { heading: m[2].trim(), body: [] }; parts.push(cur); continue; }
    if (!cur) { cur = { heading: '', body: [] }; parts.push(cur); }
    cur.body.push(line);
  }
  return parts.map(p => ({ heading: p.heading, body: p.body.join('\n').trim() })).filter(p => p.heading || p.body);
}
export function caseFileDeck(file) {
  const f = file || {};
  const name = s(f.title) || s(f.fileName) || 'Case file';
  const title = 'Assessment: ' + name;
  const text = s(f.assessment);
  const slides = [{ kicker: 'War Room · case file', title: name, lines: [
    { text: [s(f.fileName), f.pageCount ? f.pageCount + ' pages' : ''].filter(Boolean).join(' · ') },
    { text: 'A critical, even-handed assessment of both sides. Every finding points to the page it rests on; what the file does not show is said to be not shown.' },
    { text: 'For the General Chairperson only.', tone: 'warn' },
  ] }];
  if (!text) return { title, slides: slides.concat([{ kicker: 'Assessment', title: 'Not assessed yet', lines: [silent('This file has no assessment on record.')] }]) };
  for (const p of splitHeadings(text)) slides.push(...textSlides('Assessment', p.heading ? toTitle(p.heading) : 'Assessment', p.body || '(no text under this heading)'));
  return { title, slides };
}
export function patternsDeck(p) {
  const d = p || {};
  const title = 'Patterns across ' + (d.files || 'the') + ' assessed files';
  const slides = [{ kicker: 'War Room · patterns', title, lines: [{ text: 'For the General Chairperson only.', tone: 'warn' }] }];
  for (const part of splitHeadings(s(d.text))) slides.push(...textSlides('Patterns', part.heading ? toTitle(part.heading) : 'Patterns', part.body));
  return { title, slides };
}
function toTitle(h) { const t = s(h).toLowerCase(); return t.charAt(0).toUpperCase() + t.slice(1); }

// ---------------------------------------------------------------------------------------------------------------------
// 3. SECTION 6 TERMS, ROUND BY ROUND - and an agreement's dated rule changes.
// ---------------------------------------------------------------------------------------------------------------------
export function section6Deck(data) {
  const d = data || {};
  const rounds = Array.isArray(d.rounds) ? d.rounds : [];
  const title = 'Section 6 terms, round by round';
  const slides = [{ kicker: 'National rounds', title, lines: rounds.map(r => ({ text: s(r.year) + ' · ' + s(r.title) + (r.tentative ? ' (tentative)' : ''), cite: s(r.document) })) }];
  for (const t of Array.isArray(d.terms) ? d.terms : []) {
    const lines = [];
    for (const r of rounds) {
      const cell = (t.cells || {})[r.key];
      if (!cell) continue;
      const stated = s(cell.value) && s(cell.status) !== 'NOT STATED';
      lines.push(stated
        ? { text: s(r.year) + ': ' + s(cell.value).split(' | ').join('; '), cite: [cell.article ? 'Art. ' + s(cell.article).replace(/^Art\.\s*/, '') : '', cell.page ? 'p. ' + s(cell.page) : '', s(cell.status) === 'OCR' ? 'read from the scan' : s(cell.status)].filter(Boolean).join(' · ') }
        : { text: s(r.year) + ': not stated in this round', tone: 'warn' });
    }
    if (lines.length) slides.push({ kicker: 'Section 6 · term', title: s(t.term), lines });
  }
  return { title, slides };
}
const ACTION_WORDS = { not_applicable: 'not applicable', revised: 'revised', revised_in_full: 'revised in full', deleted: 'deleted' };
export function ruleChangesDeck({ agreementTitle, baseRules, mods }) {
  const title = 'Rule changes: ' + (s(agreementTitle) || 'agreement');
  const slides = [{ kicker: 'Agreement history', title, lines: [{ text: 'Each rule the platform holds a dated change for, in date order: the instrument, the date, what it did and where it applies.' }] }];
  const keys = Object.keys(mods || {}).sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0) || String(a).localeCompare(String(b)));
  for (const k of keys) {
    const list = (mods[k] || []).slice().sort((x, y) => s(x.date).localeCompare(s(y.date)));
    if (!list.length) continue;
    const base = (baseRules || {})[k] || {};
    slides.push({ kicker: 'Rule ' + k, title: s(base.title) ? 'Rule ' + k + ' - ' + s(base.title) : 'Rule ' + k, lines: list.slice(0, 8).map(m => ({
      text: [s(m.date) || 'undated', s(m.instrument), ACTION_WORDS[m.action] || s(m.action)].filter(Boolean).join(' · '),
      cite: [s(m.scopeType).replace(/_/g, ' '), s(m.scopeValue), s(m.notes)].filter(Boolean).join(' · '),
    })).concat(list.length > 8 ? [{ text: (list.length - 8) + ' more change(s) on record for this rule.', tone: 'warn' }] : []) });
  }
  return { title, slides };
}

// ---------------------------------------------------------------------------------------------------------------------
// 4. TRAINING - a module's scenes (data\training\<file>_script.json, shipped to public\training\ by
//    scripts\publish-training-slides.js).
// ---------------------------------------------------------------------------------------------------------------------
export function trainingDeck(moduleTitle, scenes) {
  const title = 'Training: ' + (s(moduleTitle) || 'module');
  const list = Array.isArray(scenes) ? scenes : [];
  const slides = [];
  list.forEach((sc, k) => {
    const lines = (Array.isArray(sc.bullets) ? sc.bullets : []).map(b => ({ text: s(b) })).filter(l => l.text);
    if (s(sc.text)) lines.push({ text: s(sc.text), tone: '' });
    const src = (Array.isArray(sc.sources) ? sc.sources : []).map(x => (typeof x === 'string' ? x : s(x.cite || x.file || x.ruling))).filter(Boolean);
    if (src.length) lines.push({ cite: 'Sources: ' + src.join('; ') });
    slides.push({ kicker: k === 0 ? 'Training' : 'Scene ' + (k + 1) + ' of ' + list.length, title: s(sc.title) || title, lines: s(sc.sub) ? [{ text: s(sc.sub) }].concat(lines) : lines });
  });
  return { title, slides };
}

// ---------------------------------------------------------------------------------------------------------------------
// 5. PRESENTING A CLAIM - the claim form's own content (src\data\penaltyClaim.js claimFormModel().form).
// ---------------------------------------------------------------------------------------------------------------------
export function claimDeck(model, claim) {
  const m = model || {};
  const f = m.form || {};
  const c = claim || {};
  const name = s(f.claimant && (f.claimant.name || f.claimant)) || s(c.claimantName) || 'The claimant';
  const number = s(f.number) || s(c.claimNumber);
  const title = 'Claim' + (number ? ' ' + number : '') + ': ' + name;
  const slides = [{ kicker: m.example ? 'EXAMPLE ONLY - NOT A REAL CLAIM' : 'Claim presentation', title: s(f.title) || title, lines: [
    { text: name + (number ? ' · ' + number : '') },
    { text: 'Agreement: ' + (s(c.applicableAgreement) || 'not on record') },
    { text: 'Date of occurrence: ' + (s(c.incidentDate) || 'not on record') + (Array.isArray(c.claimDates) && c.claimDates.length ? ' · claim dates: ' + c.claimDates.map(s).join(', ') : '') },
    s(c.presentedOn) ? { text: 'Presented to ' + (s(c.presentedTo) || 'the carrier') + ' on ' + s(c.presentedOn) } : { text: 'Not yet presented to the carrier.', tone: 'warn' },
  ] }];
  const subs = Array.isArray(f.substance) ? f.substance : [];
  let group = null;
  for (const p of subs) {
    if (!group || (s(p.label) && !p.continues)) { group = { label: s(p.label) || 'The claim', lines: [] }; slides.push({ kicker: 'The claim', title: group.label, lines: group.lines }); }
    if (s(p.text)) group.lines.push({ text: s(p.text) });
    if (s(p.quote)) group.lines.push({ quote: s(p.quote) });
  }
  if (!subs.length) {
    const facts = real((c.statementOfClaim && c.statementOfClaim.text) || c.description);
    slides.push(...(facts ? textSlides('The claim', 'The facts', facts) : [{ kicker: 'The claim', title: 'The facts', lines: [silent('No statement of the facts is on record.')] }]));
    for (const r of Array.isArray(c.matchedRules) ? c.matchedRules.filter(x => x && !x.setAsideAt) : []) {
      slides.push({ kicker: 'The rule', title: s(r.rule || r.ruleId) || 'Rule', lines: [s(r.exactLanguage) ? { quote: s(r.exactLanguage) } : silent('The rule\'s words are not on the claim.'), s(r.relevance) ? { text: s(r.relevance) } : null].filter(Boolean) });
    }
  }
  const amt = f.amount || {};
  slides.push({ kicker: 'Remedy', title: 'What is claimed', lines: [
    s(amt.text) ? { text: s(amt.text) } : null,
    s(amt.total) ? { text: 'Total: ' + s(amt.total) } : (c.amountClaimed ? { text: 'Total: $' + Number(c.amountClaimed).toLocaleString() } : silent('No amount is on record.')),
  ].filter(Boolean) });
  const att = (Array.isArray(f.attachments) ? f.attachments : []).map(a => s(a && (a.name || a.title || a))).filter(Boolean);
  if (att.length) slides.push({ kicker: 'The record', title: 'Attachments', lines: att.slice(0, 14).map(t => ({ text: t })) });
  if (Array.isArray(m.missing) && m.missing.length) slides.push({ kicker: 'Before it is sent', title: 'Not yet complete', lines: m.missing.slice(0, 10).map(t => silent(s(t.label || t.message || t))) });
  return { title, slides };
}

// ---------------------------------------------------------------------------------------------------------------------
// 6. A RELEASED VIOLATION MATRIX (ruling 310) - matrixPublished, as the General Chairperson released it.
// ---------------------------------------------------------------------------------------------------------------------
export function matrixDeck(pub) {
  const p = pub || {};
  const title = s(p.title) + ' - violation matrix';
  const entries = Array.isArray(p.entries) ? p.entries : [];
  const rules = [];
  for (const e of entries) if (!rules.includes(e.rule)) rules.push(e.rule);
  const slides = [{ kicker: 'Violation matrix', title, lines: [
    { text: entries.length + ' entries confirmed by the General Chairperson, across ' + rules.length + ' rules.' },
    { text: 'Each entry is the agreement\'s own words and how it can be violated, as the General Chairperson confirmed it. The platform interprets nothing.' },
  ] }];
  for (const r of rules) {
    const es = entries.filter(e => e.rule === r);
    for (let k = 0; k < es.length; k += 3) {
      slides.push({ kicker: 'Rule ' + (rules.indexOf(r) + 1) + ' of ' + rules.length, title: r, lines: [].concat(...es.slice(k, k + 3).map(e => [
        { text: s(e.label).toUpperCase() + (e.remedyCap ? ' · remedy capped by the rule' : '') + ' - ' + s(e.how) },
        { quote: s(e.quote), cite: 'Line ' + s(e.sourceLine) + ' · ' + s(e.confirmedLine) },
      ])) });
    }
  }
  return { title, slides };
}
